import React, { FC, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MeterData, fetchMeterData, fetchMeterDailyDataRange, fetchMeterHistoricalData } from '../services/backend/SrcfulPublicApi';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from './Tooltip';
import { DerMetadataAccordion } from './DerMetadataAccordion';
import { useGateways } from '../context/GatewayContext';
import { DailyEnergyFlowChart, CombinedPowerChart } from './PowerMeterCharts';

interface PowerMeterProps {
  lastSeen: string;
  sn: string;
  gatewayName: string;
  gatewayId: string;
  maxPowerKw: number;
}

interface MeterState {
  data: MeterData | null;
  isLoading: boolean;
  error: string | null;
}

// Helper function to convert UTC timestamp to gateway timezone
const convertToGatewayTimezone = (utcTimestamp: string, timezone?: string): Date => {
  if (!timezone) {
    return new Date(utcTimestamp);
  }
  
  const utcDate = new Date(utcTimestamp);
  
  // Use Intl.DateTimeFormat to convert to gateway timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false
  });
  
  const parts = formatter.formatToParts(utcDate);
  
  // Extract values from parts
  const year = parseInt(parts.find(part => part.type === 'year')?.value || '0');
  const month = parseInt(parts.find(part => part.type === 'month')?.value || '0') - 1; // Month is 0-based
  const day = parseInt(parts.find(part => part.type === 'day')?.value || '0');
  const hour = parseInt(parts.find(part => part.type === 'hour')?.value || '0');
  const minute = parseInt(parts.find(part => part.type === 'minute')?.value || '0');
  const second = parseInt(parts.find(part => part.type === 'second')?.value || '0');
  
  return new Date(year, month, day, hour, minute, second);
};

// Helper function to get gateway timezone date string (YYYY-MM-DD)
const getGatewayDateString = (utcTimestamp: string, timezone?: string): string => {
  const gwDate = convertToGatewayTimezone(utcTimestamp, timezone);
  return gwDate.toISOString().split('T')[0];
};

// Helper function to format selected day in gateway timezone for display
const formatSelectedDayForDisplay = (selectedDayObj: {ts_utc: string, ts_gw: string, day_str: string} | null, timezone?: string): string => {
  if (!selectedDayObj) return 'No day selected';
  
  try {
    const date = new Date(selectedDayObj.ts_gw);
    return date.toLocaleDateString();
  } catch (e) {
    return selectedDayObj.day_str; // Fallback to day string
  }
};

export const PowerMeter: FC<PowerMeterProps> = ({ 
  lastSeen, 
  sn, 
  gatewayName,
  gatewayId, 
  maxPowerKw 
}) => {
  const { gateways } = useGateways();
  
  // Get the gateway with its ownership claim
  const gatewayWithClaim = useMemo(() => {
    return gateways?.find(g => g.id === gatewayId);
  }, [gateways, gatewayId]);
  
  // Get the gateway information including timezone
  const gateway = useMemo(() => {
    return gateways?.find(g => g.id === gatewayId);
  }, [gateways, gatewayId]);
  
  const gatewayTimezone = gateway?.timeZone;

  const [meterState, setMeterState] = useState<MeterState>({
    data: null,
    isLoading: false,
    error: null
  });
  const [showAggregatedView, setShowAggregatedView] = useState<Record<string, boolean>>({});
  
  // Change selectedDay to an object with UTC and gateway timestamps
  const [selectedDay, setSelectedDay] = useState<{ts_utc: string, ts_gw: string, day_str: string} | null>(null);
  
  // Initialize selectedDay when gateway timezone is available
  useEffect(() => {
    if (gatewayTimezone && selectedDay === null) {
      const now = new Date();
      const nowInGatewayTz = convertToGatewayTimezone(now.toISOString(), gatewayTimezone);
      const startOfDayGw = new Date(nowInGatewayTz.getFullYear(), nowInGatewayTz.getMonth(), nowInGatewayTz.getDate());
      const startOfDayUtc = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      setSelectedDay({
        ts_utc: startOfDayUtc.toISOString(),
        ts_gw: startOfDayGw.toISOString(),
        day_str: startOfDayGw.toISOString().split('T')[0]
      });
    } else if (!gatewayTimezone && selectedDay === null) {
      // Fallback to browser timezone
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      setSelectedDay({
        ts_utc: startOfDay.toISOString(),
        ts_gw: startOfDay.toISOString(),
        day_str: startOfDay.toISOString().split('T')[0]
      });
    }
  }, [gatewayTimezone, selectedDay]);
  
  // Selected month/year state
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Initial state will be updated when gateway timezone is available
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() // 0-11
    };
  });
  
  // Update selectedMonth when gateway timezone becomes available
  useEffect(() => {
    if (gatewayTimezone) {
      const now = new Date();
      const nowInGatewayTz = convertToGatewayTimezone(now.toISOString(), gatewayTimezone);
      setSelectedMonth({
        year: nowInGatewayTz.getFullYear(),
        month: nowInGatewayTz.getMonth()
      });
    }
  }, [gatewayTimezone]);
  
  // Resolution state for power charts - single resolution for both
  const [powerChartsResolution, setPowerChartsResolution] = useState<'10s' | '1m' | '5m' | '10m' | '15m'>('5m');
  
  // Simplified day data state - separate for each chart
  const [dailyPowerData, setDailyPowerData] = useState<MeterData['historical']>([]);
  const [phasePowerData, setPhasePowerData] = useState<MeterData['historical']>([]);
  const [dailyPowerLoading, setDailyPowerLoading] = useState(false);
  const [phasePowerLoading, setPhasePowerLoading] = useState(false);

  // Simple function to fetch day data for specific chart
  const fetchDayDataForChart = useCallback(async (
    selectedDayObj: {ts_utc: string, ts_gw: string, day_str: string} | null, 
    resolution: '10s' | '1m' | '5m' | '10m' | '15m',
    chartType: 'daily' | 'phase'
  ) => {
    if (!selectedDayObj || !gatewayWithClaim?.ownershipClaim) return;
    
    const setLoading = chartType === 'daily' ? setDailyPowerLoading : setPhasePowerLoading;
    const setData = chartType === 'daily' ? setDailyPowerData : setPhasePowerData;
    
    setLoading(true);
    try {
      // Use the UTC timestamp directly - this is the start of day in gateway timezone but in UTC
      const startTime = new Date(selectedDayObj.ts_utc);
      const endTime = new Date(startTime.getTime() + 24 * 60 * 60 * 1000); // Add 24 hours
      
      const startTimeISO = startTime.toISOString();
      const endTimeISO = endTime.toISOString();
      
      console.log(`🚀 [${chartType.toUpperCase()}] Fetching ${selectedDayObj.day_str} with ${resolution} resolution using UTC range: ${startTimeISO} to ${endTimeISO}`);
      
      // Use the existing ownership claim from the gateway context
      const data = await fetchMeterHistoricalData(gatewayWithClaim.ownershipClaim, sn, startTimeISO, endTimeISO, resolution);
      setData(data);
      console.log(`✅ [${chartType.toUpperCase()}] Got ${data.length} points for ${selectedDayObj.day_str} @ ${resolution}`);
    } catch (error) {
      console.error(`❌ [${chartType.toUpperCase()}] Error:`, error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [sn, gatewayWithClaim]);

  // When selectedDay or power charts resolution changes, fetch data for both charts
  useEffect(() => {
    if (selectedDay) {
      fetchDayDataForChart(selectedDay, powerChartsResolution, 'daily');
      fetchDayDataForChart(selectedDay, powerChartsResolution, 'phase');
    } else {
      setDailyPowerData([]);
      setPhasePowerData([]);
    }
  }, [selectedDay, powerChartsResolution, fetchDayDataForChart]);

  // Single function to calculate month boundaries - no more duplication!
  const getMonthBoundaries = useCallback((year: number, month: number) => {
    // Get first day of selected month
    const firstDayOfMonth = new Date(year, month, 1);
    firstDayOfMonth.setHours(0, 0, 0, 0);
    
    // Get first day of NEXT month (for API end date)
    const firstDayOfNextMonth = new Date(year, month + 1, 1);
    
    
    // For past months, just add 1 day buffer
    firstDayOfNextMonth.setHours(23, 59, 59, 999);
    return { startDate: firstDayOfMonth, endDate: firstDayOfNextMonth };
  
  }, []);

  // Timeline navigation state for daily chart
  const [timelineState, setTimelineState] = useState(() => {
    // Initialize with empty state - will be populated by useEffect
    return {
      viewStartDate: new Date(),
      viewEndDate: new Date(),
      bufferedData: [] as MeterData['daily'],
      isLoadingBuffer: false,
      minAvailableDate: null as Date | null,
      maxAvailableDate: null as Date | null
    };
  });
  
  // Refs for chart references
  const isLoadingExtendedData = useRef(false);
  
  // Ref for the combined chart
  const combinedChartRef = useRef<any>(null);
  
  // Get access to the global settings
  const [settings, setSettings] = useState<{voltage: 110 | 230, fuseSize: number}>(() => {
    const savedSettings = localStorage.getItem('meterSettings');
    return savedSettings ? JSON.parse(savedSettings) : { voltage: 230, fuseSize: 25 };
  });
  
  // Check if settings existed in localStorage (to determine if metadata should be expanded by default)
  const hasExistingSettings = localStorage.getItem('meterSettings') !== null;
  
  // Calculate maximum power based on settings
  
  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('meterSettings', JSON.stringify(settings));
  }, [settings]);
  

  // Helper function to safely format numbers
  const formatNumber = (value: number | undefined | null, decimals: number = 2) => {
    if (value === undefined || value === null) return '0.000';
    return value.toFixed(decimals);
  };

  // Function to fetch extended daily data for timeline navigation
  const fetchExtendedDailyData = useCallback(async (startDate: Date, endDate: Date) => {
    // Prevent multiple simultaneous requests
    if (isLoadingExtendedData.current) {
      console.log('Skipping fetch - already loading');
      return;
    }
    
    try {
      isLoadingExtendedData.current = true;
      setTimelineState(prev => ({ ...prev, isLoadingBuffer: true }));
      
      console.log(`🔍 [FETCH] Fetching daily data from ${startDate.toISOString()} to ${endDate.toISOString()}`);
      
      const extendedData = await fetchMeterDailyDataRange(sn, startDate, endDate);
      
      console.log(`📊 [FETCH] API returned ${extendedData.length} data points:`);
      if (extendedData.length > 0) {
        const firstDate = new Date(extendedData[0].ts).toISOString().split('T')[0];
        const lastDate = new Date(extendedData[extendedData.length - 1].ts).toISOString().split('T')[0];
        console.log(`📊 [FETCH] Date range (UTC): ${firstDate} to ${lastDate}`);
        
        // Check for today in GATEWAY timezone, not UTC
        const now = new Date();
        const nowInGatewayTz = gatewayTimezone ? 
          convertToGatewayTimezone(now.toISOString(), gatewayTimezone) : 
          now;
        const todayInGatewayTz = nowInGatewayTz.toISOString().split('T')[0];
        
        // Convert API data to gateway timezone and check if today is included
        const hasToday = extendedData.some(item => {
          const gwDateStr = getGatewayDateString(item.ts, gatewayTimezone);
          return gwDateStr === todayInGatewayTz;
        });
        
        console.log(`📊 [FETCH] Today in gateway TZ (${todayInGatewayTz}) included: ${hasToday}`);
        
        // Show first and last dates in gateway timezone too
        const firstGwDate = getGatewayDateString(extendedData[0].ts, gatewayTimezone);
        const lastGwDate = getGatewayDateString(extendedData[extendedData.length - 1].ts, gatewayTimezone);
        console.log(`📊 [FETCH] Date range (Gateway TZ): ${firstGwDate} to ${lastGwDate}`);
      }
      
      setTimelineState(prev => {
        // Merge new data with existing data, removing duplicates
        const existingData = prev.bufferedData;
        const mergedData = [...existingData];
        
        extendedData.forEach(newItem => {
          const existingIndex = mergedData.findIndex(existing => existing.ts === newItem.ts);
          if (existingIndex >= 0) {
            // Update existing item
            mergedData[existingIndex] = newItem;
          } else {
            // Add new item
            mergedData.push(newItem);
          }
        });
        
        // Sort by timestamp
        mergedData.sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());
        
        const finalFirstDate = mergedData.length > 0 ? new Date(mergedData[0].ts).toISOString().split('T')[0] : 'none';
        const finalLastDate = mergedData.length > 0 ? new Date(mergedData[mergedData.length - 1].ts).toISOString().split('T')[0] : 'none';
        console.log(`📊 [MERGE] Final buffered data: ${mergedData.length} points from ${finalFirstDate} to ${finalLastDate}`);
        
        return {
          ...prev,
          bufferedData: mergedData,
          isLoadingBuffer: false,
          minAvailableDate: mergedData.length > 0 ? new Date(mergedData[0].ts) : startDate,
          maxAvailableDate: mergedData.length > 0 ? new Date(mergedData[mergedData.length - 1].ts) : endDate
        };
      });
      
    } catch (error) {
      console.error('❌ [FETCH] Error fetching extended daily data:', error);
      setTimelineState(prev => ({ ...prev, isLoadingBuffer: false }));
    } finally {
      isLoadingExtendedData.current = false;
    }
  }, [sn, gatewayTimezone]);

  // Update selectedDay when timeline view changes to ensure it's within visible range
  useEffect(() => {
    if (selectedDay) {
      const selectedDate = new Date(selectedDay.ts_gw);
      const viewStart = timelineState.viewStartDate;
      const viewEnd = timelineState.viewEndDate;
      
      // If selected day is outside the current view, update it to the last day of the view
      if (selectedDate < viewStart || selectedDate > viewEnd) {
        const endDayGw = new Date(timelineState.viewEndDate);
        const endDayUtc = new Date(endDayGw); // For now, assume same - this might need timezone conversion
        
        setSelectedDay({
          ts_utc: endDayUtc.toISOString(),
          ts_gw: endDayGw.toISOString(),
          day_str: endDayGw.toISOString().split('T')[0]
        });
      }
    }
  }, [timelineState.viewStartDate, timelineState.viewEndDate, selectedDay]);

  // Initialize timeline when component mounts and when selectedMonth changes
  useEffect(() => {
    const boundaries = getMonthBoundaries(selectedMonth.year, selectedMonth.month);
    
    console.log(`Setting up timeline for ${selectedMonth.year}-${selectedMonth.month + 1} from ${boundaries.startDate.toISOString()} to ${boundaries.endDate.toISOString()}`);
    
    setTimelineState(prev => ({
      ...prev,
      viewStartDate: boundaries.startDate,
      viewEndDate: boundaries.endDate,
      bufferedData: [], // Clear buffer when changing months
      isLoadingBuffer: false,
      minAvailableDate: null,
      maxAvailableDate: null
    }));
    
    // Fetch data for the month
    fetchExtendedDailyData(boundaries.startDate, boundaries.endDate);
  }, [selectedMonth.year, selectedMonth.month, fetchExtendedDailyData, getMonthBoundaries]); // Include all dependencies

  // Simplified handle day select
  const handleDaySelect = async (data: any) => {
    console.log(`🖱️ [DAY SELECT] Click detected:`, data);
    
    if (!data || !data.activePayload || !data.activePayload[0]) {
      console.log(`❌ [DAY SELECT] Invalid click data`);
      return;
    }
    
    const dayData = data.activePayload[0].payload;
    
    // Check if we have originalTs (UTC timestamp from the data)
    if (!dayData.originalTs) {
      console.log(`❌ [DAY SELECT] No originalTs found in day data`);
      return;
    }
    
    const utcTimestamp = dayData.originalTs; // This is the UTC timestamp of the start of the day
    const gwTimestamp = dayData.gw_ts; // This is the gateway timezone timestamp
    const dayStr = dayData.day; // YYYY-MM-DD string
    
    console.log(`🗓️ [DAY SELECT] Selected UTC timestamp: ${utcTimestamp}, GW timestamp: ${gwTimestamp}, day: ${dayStr}`);
    
    const newSelectedDay = {
      ts_utc: utcTimestamp,
      ts_gw: gwTimestamp.toISOString ? gwTimestamp.toISOString() : gwTimestamp,
      day_str: dayStr
    };
    
    // Toggle selection - if same day is clicked, deselect
    if (selectedDay && selectedDay.day_str === dayStr) {
      console.log(`🔄 [DAY SELECT] Deselecting current day: ${dayStr}`);
      setSelectedDay(null);
      return;
    }
    
    console.log(`✅ [DAY SELECT] Setting selected day:`, newSelectedDay);
    setSelectedDay(newSelectedDay);
  };

  // Get enhanced daily data with both consumption and production (selected month in gateway timezone)
  const currentMonthData = useMemo(() => {
    // Always try to use both data sources and merge them
    const timelineData = timelineState.bufferedData || [];
    const overviewData = meterState.data?.daily || [];    
    
    // Get the last day of the selected month or today if it's current month (in gateway timezone)
    const lastDayOfSelectedMonth = new Date(selectedMonth.year, selectedMonth.month + 1, 0);
    const endDay = lastDayOfSelectedMonth.getDate(); // Always show all days of the month
    
    console.log(`Generating complete month view for ${selectedMonth.year}-${selectedMonth.month + 1} days 1-${endDay} (gateway timezone: ${gatewayTimezone || 'UTC'})`);
    console.log(`📊 [MERGE] Timeline data: ${timelineData.length} points, Overview data: ${overviewData.length} points`);
    
    // Create a map of existing data by gateway timezone date string  
    const dataMap = new Map();
    
    // Add timeline data first
    timelineData.forEach(item => {
      const gwDateStr = getGatewayDateString(item.ts, gatewayTimezone);
      const enhancedItem = {
        ...item,
        gw_ts: convertToGatewayTimezone(item.ts, gatewayTimezone),
        source: 'timeline'
      };
      dataMap.set(gwDateStr, enhancedItem);
    });
    
    // Add overview data, but don't overwrite timeline data (timeline is more complete)
    overviewData.forEach(item => {
      const gwDateStr = getGatewayDateString(item.ts, gatewayTimezone);
      if (!dataMap.has(gwDateStr)) {
        const enhancedItem = {
          ...item,
          gw_ts: convertToGatewayTimezone(item.ts, gatewayTimezone),
          source: 'overview'
        };
        dataMap.set(gwDateStr, enhancedItem);
        console.log(`📊 [MERGE] Added missing day ${gwDateStr} from overview data`);
      }
    });
    
    // Generate complete month data (day 1 to end day) based on gateway timezone
    const completeMonthData = [];
    for (let day = 1; day <= endDay; day++) {
      const currentDate = new Date(selectedMonth.year, selectedMonth.month, day);
      const dateKey = currentDate.toISOString().split('T')[0]; // YYYY-MM-DD in gateway timezone
      
      // Check if we have data for this day
      const existingData = dataMap.get(dateKey);
      
      if (existingData) {
        // Use actual data with gateway timezone date
        completeMonthData.push({
          day: dateKey, // Use gateway timezone date for day selection
          consumption: existingData.consumption,
          delivery: -(existingData.delivery || 0), // Negative for chart display
          formattedDay: day.toString(), // Just the day number
          timestamp: currentDate.getTime(),
          originalTs: existingData.ts, // Keep original UTC timestamp for querying historical data
          gw_ts: existingData.gw_ts // Gateway timezone timestamp
        });
      } else {
        // Fill with zeros for missing data
        completeMonthData.push({
          day: dateKey, // Use gateway timezone date
          consumption: 0,
          delivery: 0,
          formattedDay: day.toString(), // Just the day number
          timestamp: currentDate.getTime(),
          originalTs: null, // No original data
          gw_ts: currentDate // Current date is already in gateway timezone context
        });
      }
    }
    
    console.log(`📊 [MERGE] Generated ${completeMonthData.length} days for selected month (including ${completeMonthData.filter(d => d.consumption === 0 && d.delivery === 0).length} filled with zeros)`);
    
    return completeMonthData;
  }, [
    timelineState.bufferedData,
    meterState.data?.daily,
    selectedMonth.year,
    selectedMonth.month,
    gatewayTimezone
  ]);

  // Function to fetch meter data
  const updateMeterData = useCallback(async () => {
    console.log(`📊 [OVERVIEW] Starting meter data fetch for serial number: ${sn}`);
    
    // Set loading state
    setMeterState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      console.log(`📊 [OVERVIEW] Fetching meter overview data for ${sn} (hardcoded 10m resolution)`);
      const data = await fetchMeterData(sn);
      
      console.log('📊 [OVERVIEW] Received meter overview data:', data);
      
      if (!data) {
        console.log('📊 [OVERVIEW] No data object returned from API');
        setMeterState({
          data: null,
          error: 'No data returned from API',
          isLoading: false
        });
        return;
      }
      
      if (!data.historical || data.historical.length === 0) {
        console.log('📊 [OVERVIEW] No historical data found');
        console.log('📊 [OVERVIEW] Data structure:', { 
          hasLatest: !!data.latest, 
          hasDaily: !!data.daily, 
          dailyLength: data.daily?.length || 0,
          hasHistorical: !!data.historical,
          historicalLength: data.historical?.length || 0
        });
        setMeterState({
          data: null,
          error: 'No historical data available',
          isLoading: false
        });
        return;
      }

      console.log(`📊 [OVERVIEW] Found ${data.historical.length} historical data points (last 24h @ 10m resolution)`);
      console.log(`📊 [OVERVIEW] Daily data points: ${data.daily?.length || 0}`);
      console.log(`📊 [OVERVIEW] Latest data available:`, !!data.latest);
      
      setMeterState({
        data,
        error: null,
        isLoading: false
      });
    } catch (err) {
      console.error('📊 [OVERVIEW] Error fetching meter data:', err);
      console.error('📊 [OVERVIEW] Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        serialNumber: sn
      });
      setMeterState({
        data: null,
        error: `Failed to fetch data: ${err instanceof Error ? err.message : 'Unknown error'}`,
        isLoading: false
      });
    }
  }, [sn]);

  // Set up polling for meter data every 10 seconds
  useEffect(() => {
    let isActive = true;

    const pollData = async () => {
      if (!isActive) return;
      await updateMeterData();
    };

    // Initial update
    pollData();

    // Set up interval
    const intervalId = setInterval(pollData, 10000);

    // Cleanup
    return () => {
      isActive = false;
      clearInterval(intervalId);
    };
  }, [updateMeterData]);

  // Log selectedDay changes for debugging
  useEffect(() => {
    console.log(`📅 [STATE] selectedDay changed to: ${selectedDay}`);
  }, [selectedDay]);

  return (
    <div className="px-0 py-0">
      {/* Meter Header */}
      <div className="mb-4">
        <DerMetadataAccordion
          devices={[{ sn, lastSeen }]}
          installationSettings={{
            voltage: settings.voltage,
            fuseSize: settings.fuseSize,
            onSettingsChange: setSettings
          }}
          defaultExpanded={!hasExistingSettings}
        />
      </div>

      {/* Meter Data Display */}
      {meterState.error && !meterState.data ? (
        <div className="bg-[#1D1D1D] rounded-[8px] p-4 border border-[#292929]">
          <p className="text-center text-gray-400">{meterState.error}</p>
        </div>
      ) : meterState.data?.historical && (
        <div className="space-y-3 mt-1">
          {/* Power Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Current Power Consumption */}
            <div className="bg-[#1D1D1D] rounded-[8px] p-4 border border-[#292929]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-white font-poly-sans-median">Current Power Consumption</h3>
                  <Tooltip text="Total power consumption\nacross all three phases">
                    <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                  </Tooltip>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-baseline gap-2">
                  <p className="text-5xl font-bold text-[#00FF84] font-poly-sans-median">
                    {formatNumber(meterState.data?.latest?.consumption || 0, 3)} <span className="text-gray-600 dark:text-gray-600">kW</span>
                  </p>
                  <p className="text-sm text-gray-400">
                    ({formatNumber(((meterState.data?.latest?.consumption || 0) / maxPowerKw) * 100, 1)}%)
                  </p>
                </div>
                <div className="flex items-baseline gap-2 mt-1">
                  <div className="flex items-baseline gap-1">
                    <p className="text-xs text-gray-400">Today:</p>
                    <p className="text-xs font-medium text-[#00FF84]">
                      {formatNumber(meterState.data?.daily?.[meterState.data.daily.length - 1]?.consumption || 0, 2)} kWh
                    </p>
                  </div>
                  <div className="text-gray-500 text-xs">•</div>
                  <div className="flex items-baseline gap-1">
                    <p className="text-xs text-gray-400">Yesterday:</p>
                    <p className="text-xs font-medium text-gray-400">
                      {formatNumber(meterState.data?.daily?.[meterState.data.daily.length - 2]?.consumption || 0, 2)} kWh
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Power Delivery */}
            <div className="bg-[#1D1D1D] rounded-[8px] p-4 border border-[#292929]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-white font-poly-sans-median">Current Power Delivery</h3>
                  <Tooltip text="Total power being delivered\nback to the power grid">
                    <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                  </Tooltip>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-baseline gap-2">
                  <p className="text-5xl font-bold text-blue-500 font-poly-sans-median">
                    {formatNumber(meterState.data?.latest?.delivery || 0, 3)} <span className="text-gray-600 dark:text-gray-600">kW</span>
                  </p>
                  <p className="text-sm text-gray-400">
                    ({formatNumber(((meterState.data?.latest?.delivery || 0) / maxPowerKw) * 100, 1)}%)
                  </p>
                </div>
                <div className="flex items-baseline gap-2 mt-1">
                  <div className="flex items-baseline gap-1">
                    <p className="text-xs text-gray-400">Today:</p>
                    <p className="text-xs font-medium text-blue-500">
                      {formatNumber(meterState.data?.daily?.[meterState.data.daily.length - 1]?.delivery || 0, 2)} kWh
                    </p>
                  </div>
                  <div className="text-gray-500 text-xs">•</div>
                  <div className="flex items-baseline gap-1">
                    <p className="text-xs text-gray-400">Yesterday:</p>
                    <p className="text-xs font-medium text-gray-400">
                      {formatNumber(meterState.data?.daily?.[meterState.data.daily.length - 2]?.delivery || 0, 2)} kWh
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Phase Power Distribution */}
            <div className="bg-[#1D1D1D] rounded-[8px] p-4 border border-[#292929]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm text-gray-400">Phase Distribution</h3>
                  <Tooltip text="Current power distribution across\nthe three power phases">
                    <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                  </Tooltip>
                </div>
              </div>
              <div className="space-y-3">
                {/* L1 */}
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-xs w-6">L1</span>
                  <div className="relative h-2 bg-[#E5E5E5] dark:bg-[#292929] rounded-full flex-1">
                    <div 
                      className="absolute left-0 h-full bg-green-500 rounded-full" 
                      style={{ 
                        width: `${(() => {
                          const netLoad = (meterState.data?.latest?.consumptionL1 || 0) + (meterState.data?.latest?.deliveryL1 || 0);
                          return Math.min((netLoad / maxPowerKw) * 100, 100);
                        })()}%`,
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-[#00FF84] text-right min-w-[60px]">
                    {(() => {
                      const netLoad = (meterState.data?.latest?.consumptionL1 || 0) + (meterState.data?.latest?.deliveryL1 || 0);
                      return formatNumber(netLoad, 2);
                    })()} kW
                  </span>
                </div>
                
                {/* L2 */}
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-xs w-6">L2</span>
                  <div className="relative h-2 bg-[#E5E5E5] dark:bg-[#292929] rounded-full flex-1">
                    <div 
                      className="absolute left-0 h-full bg-orange-500 rounded-full" 
                      style={{ 
                        width: `${(() => {
                          const netLoad = (meterState.data?.latest?.consumptionL2 || 0) + (meterState.data?.latest?.deliveryL2 || 0);
                          return Math.min((netLoad / maxPowerKw) * 100, 100);
                        })()}%`,
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-[#00FF84] text-right min-w-[60px]">
                    {(() => {
                      const netLoad = (meterState.data?.latest?.consumptionL2 || 0) + (meterState.data?.latest?.deliveryL2 || 0);
                      return formatNumber(netLoad, 2);
                    })()} kW
                  </span>
                </div>
                
                {/* L3 */}
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-xs w-6">L3</span>
                  <div className="relative h-2 bg-[#E5E5E5] dark:bg-[#292929] rounded-full flex-1">
                    <div 
                      className="absolute left-0 h-full bg-blue-500 rounded-full" 
                      style={{ 
                        width: `${(() => {
                          const netLoad = (meterState.data?.latest?.consumptionL3 || 0) + (meterState.data?.latest?.deliveryL3 || 0);
                          return Math.min((netLoad / maxPowerKw) * 100, 100);
                        })()}%`,
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-[#00FF84] text-right min-w-[60px]">
                    {(() => {
                      const netLoad = (meterState.data?.latest?.consumptionL3 || 0) + (meterState.data?.latest?.deliveryL3 || 0);
                      return formatNumber(netLoad, 2);
                    })()} kW
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Energy Flow Chart (Current Month) */}
          <DailyEnergyFlowChart
            currentMonthData={currentMonthData}
            selectedDay={selectedDay}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            onDaySelect={handleDaySelect}
            timelineState={timelineState}
            gatewayTimezone={gatewayTimezone}
          />

          {/* Combined Power Chart */}
          <CombinedPowerChart
            selectedDay={selectedDay}
            dailyPowerData={dailyPowerData}
            dailyPowerLoading={dailyPowerLoading}
            phasePowerData={phasePowerData}
            phasePowerLoading={phasePowerLoading}
            resolution={powerChartsResolution}
            onResolutionChange={setPowerChartsResolution}
            showAggregatedView={showAggregatedView[`total_${sn}`] || false}
            onToggleAggregatedView={() => setShowAggregatedView(prev => ({ ...prev, [`total_${sn}`]: !prev[`total_${sn}`] }))}
            sn={sn}
            formatSelectedDayForDisplay={formatSelectedDayForDisplay}
            gatewayTimezone={gatewayTimezone}
            chartRef={combinedChartRef}
          />
        </div>
      )}
    </div>
  );
};